class PropertySelector extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[id^="property-selector-button-"]');
    this.list = this.querySelector('[id^="property-selector-list-"]');
    this.options = this.querySelectorAll('.property-selector__option');
    this.selectedText = this.querySelector('[data-selected-text]');
    this.isOpen = false;
    
    this.init();
  }

  init() {
    if (!this.button || !this.list) return;
    
    // Initialize selection from URL or sessionStorage
    this.initializeSelection();
    
    // Event listeners
    this.button.addEventListener('click', this.toggleDropdown.bind(this));
    this.options.forEach(option => {
      option.addEventListener('click', this.handleOptionClick.bind(this));
      option.addEventListener('keydown', this.handleOptionKeydown.bind(this));
    });
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Listen for property changes from other selectors
    document.addEventListener('propertyChanged', this.handlePropertyChange.bind(this));
  }

  initializeSelection() {
    // Don't auto-select in theme editor
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }
    
    const urlProperty = this.getUrlParameter('property');
    const sessionProperty = sessionStorage.getItem('shopify_property_marker');
    const selectedValue = urlProperty || sessionProperty || '';
    
    if (selectedValue) {
      const option = Array.from(this.options).find(
        opt => opt.dataset.propertyValue === selectedValue
      );
      if (option) {
        this.selectOption(option);
        return;
      }
    }
    
    // Default state: show default text, no selection
    // Don't auto-select default option - let user choose
  }

  toggleDropdown(event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    this.button.setAttribute('aria-expanded', this.isOpen.toString());
    this.list.hidden = !this.isOpen;
    
    if (this.isOpen) {
      // Focus first option for keyboard navigation
      const firstOption = this.options[0];
      if (firstOption) {
        setTimeout(() => firstOption.focus(), 0);
      }
    }
  }

  handleOptionClick(event) {
    event.stopPropagation();
    const option = event.currentTarget;
    this.selectOption(option);
    this.updateProperty(option.dataset.propertyValue);
    this.closeDropdown();
  }

  handleOptionKeydown(event) {
    const option = event.currentTarget;
    const optionsArray = Array.from(this.options);
    const currentIndex = optionsArray.indexOf(option);
    
    switch(event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(option);
        this.updateProperty(option.dataset.propertyValue);
        this.closeDropdown();
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % optionsArray.length;
        optionsArray[nextIndex].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? optionsArray.length - 1 : currentIndex - 1;
        optionsArray[prevIndex].focus();
        break;
      case 'Escape':
        this.closeDropdown();
        this.button.focus();
        break;
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target) && this.isOpen) {
      this.closeDropdown();
    }
  }

  handlePropertyChange(event) {
    // Update selection if property changed externally
    const newValue = event.detail.value;
    if (newValue) {
      const option = Array.from(this.options).find(
        opt => opt.dataset.propertyValue === newValue
      );
      if (option) {
        this.selectOption(option, false); // Don't trigger update to avoid loop
      }
    } else {
      // Property cleared - reset to default without re-emitting
      this.clearSelection(false);
    }
  }

  selectOption(option, updateProperty = true) {
    // Remove previous selection
    this.options.forEach(opt => opt.classList.remove('property-selector__option--selected'));
    
    // Mark as selected
    option.classList.add('property-selector__option--selected');
    
    // Update button text
    const optionText = option.querySelector('.property-selector__option-text');
    if (optionText && this.selectedText) {
      this.selectedText.textContent = optionText.textContent.trim();
    }
    
    // Update aria-selected
    this.options.forEach(opt => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
  }

  clearSelection(emitEvent = true) {
    // Remove all selections
    this.options.forEach(opt => {
      opt.classList.remove('property-selector__option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    
    // Reset button text to default
    const defaultTextElement = this.querySelector('[data-default-text]');
    if (defaultTextElement && this.selectedText) {
      this.selectedText.textContent = defaultTextElement.textContent.trim();
    }
    
    // Clear property
    if (emitEvent) {
      this.updateProperty('');
    }
  }

  updateProperty(value) {
    // Update URL parameter
    const url = new URL(window.location);
    if (value && value !== '') {
      url.searchParams.set('property', value);
    } else {
      url.searchParams.delete('property');
    }
    window.history.replaceState({}, '', url);
    
    // Store in sessionStorage
    const option = Array.from(this.options).find(
      opt => opt.dataset.propertyValue === value
    );
    if (value && value !== '') {
      sessionStorage.setItem('shopify_property_marker', value);
      if (option) {
        sessionStorage.setItem('shopify_property_key', option.dataset.propertyKey || '');
      }
    } else {
      sessionStorage.removeItem('shopify_property_marker');
      sessionStorage.removeItem('shopify_property_key');
    }
    
    // Dispatch custom event
    const event = new CustomEvent('propertyChanged', {
      detail: {
        value: value,
        key: option ? option.dataset.propertyKey : ''
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  closeDropdown() {
    this.isOpen = false;
    this.button.setAttribute('aria-expanded', 'false');
    this.list.hidden = true;
  }

  getUrlParameter(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }
}

customElements.define('property-selector', PropertySelector);

// Initialize on DOMContentLoaded for any selectors already in the DOM
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-property-selector]').forEach(element => {
    if (!element.closest('property-selector')) {
      const selector = document.createElement('property-selector');
      element.parentNode.insertBefore(selector, element);
      selector.appendChild(element);
    }
  });
});
